import React, { FC, useState } from 'react';
import type { OrganisationInvitation } from '@dreamai/types';
import { SEAT_LABELS, formatDate } from './organisationLabels';
import './organisation-invitations.scss';

export const ONE_WAY_DOOR =
  'Joining an organisation is permanent: you cannot leave it or join another one later.';

interface OrganisationInvitationsProps {
  invitations: OrganisationInvitation[];
  busyId: string | null;
  onRespond: (
    invitation: OrganisationInvitation,
    answer: 'accept' | 'decline',
  ) => Promise<void>;
}

/**
 * Pending invitations for a user who is not yet in an organisation. Accepting
 * is a one-way door, so it always goes through an explicit second step.
 */
const OrganisationInvitations: FC<OrganisationInvitationsProps> = ({
  invitations,
  busyId,
  onRespond,
}) => {
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <div className='organisation-card organisation-invitations'>
      <h3 className='organisation-card-title'>Your invitations</h3>
      <ul className='organisation-invitations-list'>
        {invitations.map((invitation) => {
          const busy = busyId === invitation.membershipId;
          const meta = [
            invitation.invitedByName
              ? `Invited by ${invitation.invitedByName}`
              : null,
            invitation.seatKind === 'directory' ? SEAT_LABELS.directory : null,
            formatDate(invitation.invitedAt),
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <li
              key={invitation.membershipId}
              className='organisation-invitation'
              data-testid='organisation-invitation'
            >
              <div className='organisation-invitation-info'>
                <span className='organisation-invitation-name'>
                  {invitation.organisation.name}
                </span>
                <span className='organisation-invitation-meta'>{meta}</span>
              </div>
              {confirming === invitation.membershipId ? (
                <div className='organisation-invitation-confirm'>
                  <p>
                    Join <strong>{invitation.organisation.name}</strong>?{' '}
                    {ONE_WAY_DOOR} Its admins will be able to see your name,
                    seat, status and when you were last active — nothing about
                    what you and your secretary discuss.
                  </p>
                  <div className='organisation-invitation-actions'>
                    <button
                      type='button'
                      className='btn-primary'
                      disabled={busy}
                      onClick={async () => {
                        await onRespond(invitation, 'accept');
                        setConfirming(null);
                      }}
                    >
                      {busy ? 'Joining…' : 'Yes, join permanently'}
                    </button>
                    <button
                      type='button'
                      className='btn-secondary'
                      disabled={busy}
                      onClick={() => setConfirming(null)}
                    >
                      Not now
                    </button>
                  </div>
                </div>
              ) : (
                <div className='organisation-invitation-actions'>
                  <button
                    type='button'
                    className='btn-primary'
                    disabled={busy}
                    onClick={() => setConfirming(invitation.membershipId)}
                  >
                    Accept
                  </button>
                  <button
                    type='button'
                    className='btn-secondary'
                    disabled={busy}
                    onClick={() => onRespond(invitation, 'decline')}
                  >
                    {busy ? '…' : 'Decline'}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OrganisationInvitations;
